import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchVacations,
  saveVacations,
  fetchProductionCalendar,
  createUnpaidLeave,
  updateUnpaidLeave,
  deleteUnpaidLeave,
  exportSubstitutionsXlsx,
  getApiErrorMessage,
} from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Loading from '../components/Loading';
import VacationMonthGrid from './VacationMonthGrid';
import VacationYearTimeline from './VacationYearTimeline';
import VacationSubstitutions from './VacationSubstitutions';
import UnpaidLeaveDialog from './UnpaidLeaveDialog';
import {
  defaultMonthIndex,
  formatUnpaidLeave,
  moscowToday,
  unpaidCoversDate,
} from './vacationDates';
import {
  conflictIdentity,
  employeeConflictKind,
  findConflicts,
  formatConflictLine,
  newConflicts,
  overlapIsInRuleYears,
  periodConflictKind,
} from './vacationConflicts';

const MONTHS = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];

const MONTHS_FULL = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

// Цвет зависит только от статуса
const getVacationColor = (period) => {
  const status = getPeriodStatus(period);
  if (status === 'active') return '#35c79a';    // зелёный — сейчас
  if (status === 'overdue') return '#7e8a9a';   // серый — прошёл
  return '#5c87b0';                              // синий — запланирован
};

const CONFLICT_COLORS = {
  existing: '#f59e0b',
  new: '#ef4444',
};

function formatDate(d) {
  if (!d) return '';
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function formatDateFull(d) {
  if (!d) return '';
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function parseDateInput(str) {
  if (!str) return null;
  return str;
}

function formatDateInput(d) {
  if (!d) return '';
  if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function calcEnd(start, days) {
  if (!start || !days) return null;
  const s = new Date(start + 'T00:00:00');
  const result = new Date(s.getFullYear(), s.getMonth(), s.getDate() + days - 1);
  return formatDateInput(result);
}

function calcDays(start, end) {
  if (!start || !end) return null;
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
}

function calcRemaining(total, periods) {
  if (total === null || total === undefined) return null;
  const used = periods.reduce((sum, p) => sum + (p.days || 0), 0);
  return total - used;
}

function isDateInPeriod(date, period) {
  if (!period.start || !period.end) return false;
  const d = new Date(date + 'T00:00:00');
  const start = new Date(period.start + 'T00:00:00');
  const end = new Date(period.end + 'T00:00:00');
  return d >= start && d <= end;
}

function todayAtMidnight() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

function getPeriodStatus(period) {
  if (!period.start || !period.end) return null;
  const now = todayAtMidnight();
  const start = new Date(period.start + 'T00:00:00');
  const end = new Date(period.end + 'T00:00:00');
  
  if (now >= start && now <= end) return 'active';
  if (now > end) return 'overdue';
  return 'planned';
}

function normalizeVacationSheets(data) {
  const safeSheets = Array.isArray(data) ? data : [];
  return safeSheets.map((sheet) => {
    const employees = Array.isArray(sheet?.employees) ? sheet.employees.map((emp) => {
      const periods = Array.isArray(emp?.periods) ? emp.periods.map((p) => ({
        ...p,
        days: (p.start && p.end) ? calcDays(p.start, p.end) : p.days,
      })) : [];
      return {
        ...emp,
        name: emp?.name || 'Без имени',
        periods,
        unpaid_leaves: Array.isArray(emp?.unpaid_leaves) ? emp.unpaid_leaves : [],
        remaining: emp.remaining !== null && emp.remaining !== undefined
          ? emp.remaining
          : calcRemaining(emp.total_days, periods),
      };
    }) : [];
    return {
      ...sheet,
      year: String(sheet?.year ?? ''),
      name: sheet?.name || `Отпуска ${sheet?.year ?? ''}`,
      employees,
      conflicts: Array.isArray(sheet?.conflicts) ? sheet.conflicts : [],
      staff_coverage: Array.isArray(sheet?.staff_coverage) ? sheet.staff_coverage : [],
    };
  });
}

export default function VacationCalendar() {
  const [sheets, setSheets] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedEmployees, setEditedEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(() => defaultMonthIndex(moscowToday().slice(0, 4), moscowToday()));
  const [showDensityChart, setShowDensityChart] = useState(false);
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarError, setCalendarError] = useState(null);
  const [leaveDialog, setLeaveDialog] = useState(null);

  useEffect(() => {
    const today = moscowToday();
    const todayYear = today.slice(0, 4);
    Promise.all([
      fetchVacations(),
      fetchProductionCalendar(todayYear).catch(() => null),
    ])
      .then(([data, calendar]) => {
        const processedData = normalizeVacationSheets(data);
        setSheets(processedData);
        const year = processedData.some((sheet) => sheet.year === todayYear)
          ? todayYear
          : (processedData[0]?.year || '');
        setSelectedYear(year);
        setSelectedMonth(defaultMonthIndex(year, today));
        if (calendar && String(calendar.year) === String(year)) {
          setCalendarDays(Array.isArray(calendar.days) ? calendar.days : []);
          setCalendarError(null);
        } else if (year) {
          return fetchProductionCalendar(year)
            .then((yearCalendar) => {
              setCalendarDays(Array.isArray(yearCalendar.days) ? yearCalendar.days : []);
              setCalendarError(null);
            })
            .catch(() => {
              setCalendarDays([]);
              setCalendarError('Не удалось загрузить производственный календарь');
            });
        } else if (calendar === null) {
          setCalendarDays([]);
          setCalendarError('Не удалось загрузить производственный календарь');
        }
      })
      .catch((err) => {
        console.error('Failed to load vacations:', err);
        setError('Не удалось загрузить график отпусков');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedYear) return undefined;
    let cancelled = false;
    fetchProductionCalendar(selectedYear)
      .then((data) => {
        if (cancelled) return;
        setCalendarDays(Array.isArray(data.days) ? data.days : []);
        setCalendarError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setCalendarDays([]);
        setCalendarError('Не удалось загрузить производственный календарь');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  const currentSheet = useMemo(
    () => sheets.find((s) => s.year === selectedYear),
    [sheets, selectedYear]
  );

  const serverConflicts = (currentSheet?.conflicts || []).filter((item) => (
    overlapIsInRuleYears(item.overlap_start, item.overlap_end)
  ));
  const staffCoverage = currentSheet?.staff_coverage || [];

  const localConflicts = useMemo(() => {
    const employees = editMode ? editedEmployees : (currentSheet?.employees || []);
    return findConflicts(staffCoverage, employees);
  }, [editMode, editedEmployees, currentSheet, staffCoverage]);

  const freshConflicts = useMemo(
    () => newConflicts(serverConflicts, localConflicts),
    [serverConflicts, localConflicts]
  );

  const displayConflicts = editMode ? localConflicts : serverConflicts;

  useEffect(() => {
    if (currentSheet) {
      setEditedEmployees(JSON.parse(JSON.stringify(currentSheet.employees)));
    }
  }, [currentSheet, editMode]);

  const filteredEmployees = useMemo(() => {
    let employees = editMode ? editedEmployees : (currentSheet?.employees || []);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      employees = employees.filter(e => e.name.toLowerCase().includes(query));
    }

    if (statusFilter !== 'all') {
      const now = todayAtMidnight();
      employees = employees.filter(e => {
        const hasActive = e.periods.some(p => {
          if (!p.start || !p.end) return false;
          const start = new Date(p.start + 'T00:00:00');
          const end = new Date(p.end + 'T00:00:00');
          return now >= start && now <= end;
        });
        
        if (statusFilter === 'active') return hasActive;
        if (statusFilter === 'inactive') return !hasActive;
        if (statusFilter === 'low_balance') return (e.remaining !== null && e.remaining < 7);
        if (statusFilter === 'over_limit') return (e.remaining !== null && e.remaining < 0);
        return true;
      });
    }
    
    return employees;
  }, [currentSheet, editedEmployees, editMode, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    if (!currentSheet) return null;
    const employees = currentSheet.employees;
    const total = employees.length;
    const now = todayAtMidnight();
    const todayStr = moscowToday();
    const onVacation = employees.filter((e) =>
      e.periods.some(
        (p) => p.start && p.end && new Date(p.start + 'T00:00:00') <= now && new Date(p.end + 'T00:00:00') >= now
      )
    ).length;
    const onUnpaid = employees.filter((e) =>
      (e.unpaid_leaves || []).some((leave) => unpaidCoversDate(leave, todayStr))
    ).length;
    const overLimit = employees.filter((e) => e.remaining !== null && e.remaining < 0).length;
    const lowBalance = employees.filter((e) => e.remaining !== null && e.remaining >= 0 && e.remaining < 7).length;
    const plannedDays = employees.reduce(
      (sum, e) => sum + e.periods.reduce((pSum, p) => pSum + (p.days || 0), 0),
      0
    );
    const totalBalance = employees.reduce((sum, e) => sum + (e.total_days || 0), 0);
    const plannedPercent = totalBalance > 0 ? Math.round((plannedDays / totalBalance) * 100) : 0;
    
    // Find next vacation
    let nextVacation = null;
    const futurePeriods = [];
    employees.forEach(e => {
      e.periods.forEach(p => {
        if (p.start) {
          const start = new Date(p.start + 'T00:00:00');
          if (start > now) {
            futurePeriods.push({
              employee: e.name,
              start: p.start,
              days: p.days,
            });
          }
        }
      });
    });
    if (futurePeriods.length > 0) {
      futurePeriods.sort((a, b) => new Date(a.start) - new Date(b.start));
      nextVacation = futurePeriods[0];
    }
    
    return { total, onVacation, onUnpaid, overLimit, lowBalance, plannedPercent, nextVacation };
  }, [currentSheet]);

  const densityData = useMemo(() => {
    if (!currentSheet) return [];
    
    // Extract year from sheet name (e.g., "Отпуск 2026" -> 2026)
    const yearMatch = currentSheet.year.match(/\d{4}/);
    const year = yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear();
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    const daysInYear = isLeap ? 366 : 365;
    
    const data = [];
    for (let day = 1; day <= daysInYear; day++) {
      const date = new Date(year, 0, day);
      const dateStr = formatDateInput(date);
      
      let count = 0;
      currentSheet.employees.forEach(e => {
        const isOnVacation = e.periods.some(p => isDateInPeriod(dateStr, p));
        if (isOnVacation) count++;
      });
      
      const month = date.getMonth();
      data.push({
        day,
        date: dateStr,
        month,
        monthName: MONTHS[month],
        count,
      });
    }
    
    return data;
  }, [currentSheet]);

  const monthlyDensity = useMemo(() => {
    const monthly = {};
    densityData.forEach(d => {
      if (!monthly[d.month]) {
        monthly[d.month] = { month: d.month, monthName: d.monthName, maxCount: 0, avgCount: 0, days: 0 };
      }
      monthly[d.month].maxCount = Math.max(monthly[d.month].maxCount, d.count);
      monthly[d.month].avgCount += d.count;
      monthly[d.month].days++;
    });
    
    return Object.values(monthly).map(m => ({
      ...m,
      avgCount: Math.round(m.avgCount / m.days * 10) / 10,
    }));
  }, [densityData]);

  const updateEmployee = (employeeName, field, value) => {
    setEditedEmployees((prev) => {
      const idx = prev.findIndex((e) => e.name === employeeName);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'total_days' || field === 'periods') {
        next[idx].remaining = calcRemaining(next[idx].total_days, next[idx].periods);
      }
      return next;
    });
  };

  const updatePeriod = (employeeName, periodIdx, field, value) => {
    setEditedEmployees((prev) => {
      const empIdx = prev.findIndex((e) => e.name === employeeName);
      if (empIdx < 0) return prev;
      const next = [...prev];
      const emp = { ...next[empIdx] };
      const periods = [...emp.periods];
      periods[periodIdx] = { ...periods[periodIdx], [field]: value };

      const p = periods[periodIdx];
      if (field === 'start' || field === 'days') {
        if (p.start && p.days) {
          periods[periodIdx].end = calcEnd(p.start, p.days);
        }
      } else if (field === 'end') {
        if (p.start && p.end) {
          periods[periodIdx].days = calcDays(p.start, p.end);
        }
      }

      emp.periods = periods;
      emp.remaining = calcRemaining(emp.total_days, emp.periods);
      next[empIdx] = emp;
      return next;
    });
  };

  const addPeriod = (employeeName) => {
    setEditedEmployees((prev) => {
      const empIdx = prev.findIndex((e) => e.name === employeeName);
      if (empIdx < 0) return prev;
      const next = [...prev];
      const emp = { ...next[empIdx] };
      emp.periods = [...emp.periods, { start: null, days: null, end: null, application_date: null }];
      next[empIdx] = emp;
      return next;
    });
  };

  const removePeriod = (employeeName, periodIdx) => {
    setEditedEmployees((prev) => {
      const empIdx = prev.findIndex((e) => e.name === employeeName);
      if (empIdx < 0) return prev;
      const next = [...prev];
      const emp = { ...next[empIdx] };
      emp.periods = emp.periods.filter((_, i) => i !== periodIdx);
      emp.remaining = calcRemaining(emp.total_days, emp.periods);
      next[empIdx] = emp;
      return next;
    });
  };

  const handleSave = async () => {
    if (freshConflicts.length > 0) {
      setSaveError(freshConflicts.map((item) => `Нельзя: ${formatConflictLine(item)}`).join('; '));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const cleanEmployees = editedEmployees.map((emp) => ({
        ...emp,
        hire_date: emp.hire_date || null,
        total_days: emp.total_days ?? null,
        remaining: emp.remaining ?? null,
        periods: emp.periods
          .filter((p) => p.start && p.end && p.days)
          .map((p) => ({
            start: p.start,
            days: p.days,
            end: p.end,
            application_date: p.application_date || null,
          })),
      }));

      const updatedSheet = { ...currentSheet, employees: cleanEmployees };
      await saveVacations([updatedSheet]);
      const refreshed = normalizeVacationSheets(await fetchVacations());
      setSheets(refreshed);
      setEditMode(false);
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveError(getApiErrorMessage(err, 'Не удалось сохранить изменения'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSaveError(null);
    if (currentSheet) {
      setEditedEmployees(JSON.parse(JSON.stringify(currentSheet.employees)));
    }
  };

  const handleExportYear = async () => {
    if (!currentSheet) return;
    setExportError(null);
    setExportBusy(true);
    try {
      await exportSubstitutionsXlsx(
        Number(currentSheet.year),
        undefined,
        `замены_${currentSheet.year}.xlsx`
      );
    } catch (err) {
      setExportError(getApiErrorMessage(err, 'Не удалось скачать файл'));
    } finally {
      setExportBusy(false);
    }
  };

  const handleExport = () => {
    if (!currentSheet) return;
    
    const rows = [
      ['ФИО', 'Дата приема', 'Положено дней', 'Использовано', 'Остаток', 'Периоды отпуска', 'Отгулы'],
    ];
    
    currentSheet.employees.forEach(emp => {
      const used = emp.periods.reduce((s, p) => s + (p.days || 0), 0);
      const periods = emp.periods.map(p => 
        `${formatDate(p.start)} - ${formatDate(p.end)} (${p.days} дн.)`
      ).join('; ');
      const unpaid = (emp.unpaid_leaves || []).map(formatUnpaidLeave).filter(Boolean).join('; ');
      
      rows.push([
        emp.name,
        formatDate(emp.hire_date),
        emp.total_days || '',
        used,
        emp.remaining ?? '',
        periods,
        unpaid,
      ]);
    });
    
    const csv = rows.map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `отпуска_${currentSheet.year}.csv`;
    link.click();
  };

  const handleYearChange = (year) => {
    setExportError(null);
    setSelectedYear(year);
    setSelectedMonth(defaultMonthIndex(year, moscowToday()));
  };

  const changeViewMode = (mode) => {
    setExportError(null);
    setViewMode(mode);
  };

  const shiftMonth = (delta) => {
    setSelectedMonth((prev) => Math.min(11, Math.max(0, prev + delta)));
  };

  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setViewMode('month');
  };

  const refreshSheets = async () => {
    const refreshed = normalizeVacationSheets(await fetchVacations());
    setSheets(refreshed);
  };

  const handleCellClick = ({ employee, dateStr, part, existingLeave }) => {
    if (editMode) return;
    setLeaveDialog({
      employee,
      dateStr,
      initial: existingLeave
        ? {
          id: existingLeave.id,
          start: existingLeave.start,
          end: existingLeave.end,
          day_part: existingLeave.day_part,
        }
        : { start: dateStr, end: dateStr, day_part: part || 'full' },
      error: null,
    });
  };

  const handleLeaveSave = async (payload) => {
    if (!leaveDialog) return;
    try {
      if (payload.id != null) {
        await updateUnpaidLeave(payload.id, {
          start: payload.start,
          end: payload.end,
          day_part: payload.day_part,
        });
      } else {
        await createUnpaidLeave({
          employee_id: leaveDialog.employee.employee_id,
          start: payload.start,
          end: payload.end,
          day_part: payload.day_part,
        });
      }
      await refreshSheets();
      setLeaveDialog(null);
    } catch (err) {
      setLeaveDialog((prev) => (
        prev ? { ...prev, error: getApiErrorMessage(err, 'Не удалось сохранить отгул') } : prev
      ));
    }
  };

  const handleLeaveDelete = async () => {
    if (!leaveDialog?.initial?.id) return;
    try {
      await deleteUnpaidLeave(leaveDialog.initial.id);
      await refreshSheets();
      setLeaveDialog(null);
    } catch (err) {
      setLeaveDialog((prev) => (
        prev ? { ...prev, error: getApiErrorMessage(err, 'Не удалось удалить отгул') } : prev
      ));
    }
  };

  if (loading) return <Loading text='Загрузка графика отпусков...' />;
  if (error) return <div className='error'>{error}</div>;
  if (!currentSheet) return <div className='empty-state'>Нет данных об отпусках</div>;

  const displayEmployees = filteredEmployees;
  const isSubstitutions = viewMode === 'substitutions';

  return (
    <div className='vacation-calendar'>
      {/* Header */}
      <div className='section-header'>
        <div>
          <h2>График отпусков</h2>
          <div className="section-subtitle">
            {currentSheet.year} год · {currentSheet.employees.length} сотрудников
          </div>
        </div>
        <div className='controls'>
          <select value={selectedYear} onChange={(e) => handleYearChange(e.target.value)} disabled={editMode}>
            {sheets.map((s) => (
              <option key={s.year} value={s.year}>{s.name}</option>
            ))}
          </select>
          <div className='vacation-view-toggle' role='group' aria-label='Режим графика'>
            <button
              type='button'
              className={`btn btn-secondary${viewMode === 'month' ? ' vacation-view-toggle--active' : ''}`}
              aria-pressed={viewMode === 'month'}
              onClick={() => changeViewMode('month')}
            >
              Месяц
            </button>
            <button
              type='button'
              className={`btn btn-secondary${viewMode === 'year' ? ' vacation-view-toggle--active' : ''}`}
              aria-pressed={viewMode === 'year'}
              onClick={() => changeViewMode('year')}
            >
              Год
            </button>
            <button
              type='button'
              className={`btn btn-secondary${viewMode === 'substitutions' ? ' vacation-view-toggle--active' : ''}`}
              aria-pressed={viewMode === 'substitutions'}
              onClick={() => changeViewMode('substitutions')}
              disabled={editMode}
            >
              Замены
            </button>
          </div>
          
          {!editMode && !isSubstitutions && (
            <button className='btn btn-secondary' onClick={handleExport}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Экспорт
            </button>
          )}
          {!editMode && isSubstitutions && (
            <button className='btn btn-secondary' onClick={handleExportYear} disabled={exportBusy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Экспорт года
            </button>
          )}
          
          {!isSubstitutions && (editMode ? (
            <>
              <button
                className='btn btn-primary'
                onClick={handleSave}
                disabled={saving || freshConflicts.length > 0}
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button className='btn btn-secondary' onClick={handleCancel} disabled={saving}>
                Отмена
              </button>
            </>
          ) : (
            <button className='btn btn-primary' onClick={() => { setSaveError(null); setLeaveDialog(null); setEditMode(true); }}>
              Редактировать
            </button>
          ))}
        </div>
      </div>

      {isSubstitutions ? (
        <>
          {exportError && (
            <div className='error' role='status'>Ошибка экспорта: {exportError}</div>
          )}
          <VacationSubstitutions year={Number(currentSheet.year)} />
        </>
      ) : (
        <>
      {saveError && (
        <div className='error' style={{ marginBottom: 16 }}>
          Ошибка сохранения: {saveError}
        </div>
      )}

      {displayConflicts.length > 0 && (
        <div
          className={`vacation-conflict-banner${editMode && freshConflicts.length > 0 ? ' vacation-conflict-banner--new' : ''}`}
          role="status"
        >
          <strong>
            {editMode && freshConflicts.length > 0
              ? 'Новые пересечения отпусков у бухгалтеров одного клиента — сохранить нельзя'
              : 'Пересечение отпусков у бухгалтеров одного клиента'}
          </strong>
          <ul>
            {displayConflicts.map((item) => {
              const isNew = freshConflicts.some(
                (fresh) => conflictIdentity(fresh) === conflictIdentity(item)
              );
              return (
                <li key={conflictIdentity(item)}>
                  {isNew && editMode ? <span className='vacation-conflict-new-mark'>Новое. </span> : null}
                  {formatConflictLine(item)}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className='stats-bar'>
          <div className='stat-card'>
            <span className='stat-value'>{stats.total}</span>
            <span className='stat-label'>Сотрудников</span>
          </div>
          <div className='stat-card'>
            <span className='stat-value' style={{ color: stats.onVacation > 0 ? '#10b981' : 'var(--text-muted)' }}>
              {stats.onVacation}
            </span>
            <span className='stat-label'>В отпуске сейчас</span>
          </div>
          <div className='stat-card'>
            <span className='stat-value' style={{ color: stats.onUnpaid > 0 ? '#8b5cf6' : 'var(--text-muted)' }}>
              {stats.onUnpaid}
            </span>
            <span className='stat-label'>На отгуле сейчас</span>
          </div>
          <div className='stat-card'>
            <span className='stat-value' style={{ color: stats.overLimit > 0 ? 'var(--score-low)' : 'var(--text-primary)' }}>
              {stats.overLimit}
            </span>
            <span className='stat-label'>Превышен лимит</span>
          </div>
          <div className='stat-card'>
            <span className='stat-value' style={{ color: stats.lowBalance > 0 ? 'var(--score-medium)' : 'var(--text-primary)' }}>
              {stats.lowBalance}
            </span>
            <span className='stat-label'>Остаток меньше 7 дней</span>
          </div>
          <div className='stat-card'>
            <span className='stat-value'>{stats.plannedPercent}%</span>
            <span className='stat-label'>Запланировано от лимита</span>
            <div className='progress-container' style={{ marginTop: 8 }}>
              <div className='progress-bar' style={{ height: 4 }}>
                <div className='progress-fill' style={{ width: `${Math.min(stats.plannedPercent, 100)}%` }} />
              </div>
            </div>
          </div>
          {stats.nextVacation && (
            <div className='stat-card'>
              <span className='stat-value' style={{ fontSize: 18, lineHeight: '1.4' }}>
                {stats.nextVacation.employee.split(' ')[0]}
              </span>
              <span className='stat-label'>Ближайший отпуск</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                {formatDate(stats.nextVacation.start)} | {stats.nextVacation.days} дн.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {!editMode && (
        <div className='data-card' style={{ marginBottom: 24, padding: 16 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <input
                type='text'
                placeholder='Поиск по имени...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='form-input'
                style={{ width: '100%' }}
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minWidth: 180 }}>
              <option value='all'>Все сотрудники</option>
              <option value='active'>В отпуске сейчас</option>
              <option value='inactive'>Не в отпуске</option>
              <option value='low_balance'>Остаток меньше 7 дн</option>
              <option value='over_limit'>Превышен лимит</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)' }}>
              <input
                type='checkbox'
                checked={showDensityChart}
                onChange={(e) => setShowDensityChart(e.target.checked)}
              />
              График загрузки
            </label>
          </div>
        </div>
      )}

      {/* Density Chart */}
      {showDensityChart && !editMode && monthlyDensity.length > 0 && (
        <div className='data-card' style={{ marginBottom: 24 }}>
          <div className='data-card-header'>
            <div className='data-card-title'>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <line x1='18' y1='20' x2='18' y2='10'/>
                <line x1='12' y1='20' x2='12' y2='4'/>
                <line x1='6' y1='20' x2='6' y2='14'/>
              </svg>
              Загрузка по месяцам (человек в отпуске)
            </div>
          </div>
          <div className='data-card-body'>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyDensity}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis 
                  dataKey="monthName" 
                  tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                  axisLine={{ stroke: 'var(--border-color)' }}
                />
                <YAxis 
                  tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                  axisLine={{ stroke: 'var(--border-color)' }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    color: 'var(--text-primary)',
                  }}
                  formatter={(value) => [`${value} чел.`, 'Макс. в отпуске']}
                />
                <Bar dataKey="maxCount" radius={[4, 4, 0, 0]} fill="var(--accent-primary)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {calendarError && (
        <div className='error' style={{ marginBottom: 16 }} role='status'>
          {calendarError}
        </div>
      )}

      <div className='vacation-legend' aria-label='Легенда'>
        <span className='vacation-legend-item'><i style={{ background: '#5c87b0' }} />Отпуск</span>
        <span className='vacation-legend-item'><i style={{ background: '#8b5cf6' }} />Отгул</span>
        <span className='vacation-legend-item'><i style={{ background: 'rgba(239, 68, 68, 0.45)' }} />Праздник</span>
        <span className='vacation-legend-item'><i style={{ background: 'rgba(148, 163, 184, 0.45)' }} />Выходной</span>
        <span className='vacation-legend-item'><i style={{ background: 'rgba(245, 158, 11, 0.45)' }} />Предпраздничный</span>
      </div>

      {viewMode === 'month' && (
        <div className='data-card' style={{ overflowX: 'auto', marginBottom: 24 }}>
          <div className='data-card-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div className='data-card-title'>
              {MONTHS_FULL[selectedMonth]} {currentSheet.year}
            </div>
            <div className='vacation-month-nav' role='group' aria-label='Месяц'>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={() => shiftMonth(-1)}
                disabled={selectedMonth <= 0}
                aria-label='Предыдущий месяц'
              >
                ←
              </button>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={() => shiftMonth(1)}
                disabled={selectedMonth >= 11}
                aria-label='Следующий месяц'
              >
                →
              </button>
            </div>
          </div>
          <div className='data-card-body' style={{ padding: 0 }}>
            <VacationMonthGrid
              employees={displayEmployees}
              year={Number(currentSheet.year)}
              monthIndex={selectedMonth}
              days={calendarDays}
              editVacations={editMode}
              onCellClick={handleCellClick}
            />
          </div>
        </div>
      )}

      {viewMode === 'year' && (
        <VacationYearTimeline
          employees={displayEmployees}
          year={Number(currentSheet.year)}
          onMonthSelect={handleMonthSelect}
          editMode={editMode}
          displayConflicts={displayConflicts}
          freshConflicts={freshConflicts}
        />
      )}

      {leaveDialog && (
        <UnpaidLeaveDialog
          key={leaveDialog.initial.id ?? leaveDialog.dateStr}
          employeeName={leaveDialog.employee.name}
          initial={leaveDialog.initial}
          error={leaveDialog.error}
          onSave={handleLeaveSave}
          onDelete={leaveDialog.initial.id != null ? handleLeaveDelete : undefined}
          onClose={() => setLeaveDialog(null)}
        />
      )}

      {/* Details Table */}
      {editMode && (
      <div className='data-card'>
        <div className='data-card-header'>
          <div className='data-card-title'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='8' y1='6' x2='21' y2='6'></line>
              <line x1='8' y1='12' x2='21' y2='12'></line>
              <line x1='8' y1='18' x2='21' y2='18'></line>
              <line x1='3' y1='6' x2='3.01' y2='6'></line>
              <line x1='3' y1='12' x2='3.01' y2='12'></line>
              <line x1='3' y1='18' x2='3.01' y2='18'></line>
            </svg>
            Детали по сотрудникам
          </div>
        </div>
        <div className='data-card-body'>
          <div className='table-container'>
            <table className='score-table'>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Положено</th>
                  <th>Периоды отпуска</th>
                  <th>Использовано</th>
                  <th>Остаток</th>
                  <th>Статус</th>
                  {editMode && <th>Действия</th>}
                </tr>
              </thead>
              <tbody>
                {displayEmployees.map((emp, idx) => {
                  const used = emp.periods.reduce((s, p) => s + (p.days || 0), 0);
                  const hasActive = emp.periods.some(p => getPeriodStatus(p) === 'active');
                  const hasPlanned = emp.periods.some(p => getPeriodStatus(p) === 'planned');
                  const rowConflictKind = employeeConflictKind(
                    emp.employee_id,
                    displayConflicts,
                    editMode ? freshConflicts : []
                  );
                  
                  return (
                    <tr key={idx} className={rowConflictKind ? `vacation-table-row--conflict-${rowConflictKind}` : undefined}>
                      <td>
                        <div className='employee-cell'>
                          <div className='employee-avatar' style={{
                            background: hasActive 
                              ? 'linear-gradient(135deg, #10b981, #059669)' 
                              : undefined,
                            boxShadow: hasActive ? '0 0 0 2px #10b98140' : undefined,
                          }}>
                            {emp.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                          </div>
                          <div className='employee-info'>
                            <span className='employee-name'>{emp.name}</span>
                            {emp.hire_date && (
                              <span className='employee-role'>
                                Принят: {formatDate(emp.hire_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {editMode ? (
                          <input
                            type='number'
                            value={emp.total_days ?? ''}
                            onChange={(e) => updateEmployee(emp.name, 'total_days', e.target.value ? Number(e.target.value) : null)}
                            className='form-input'
                            style={{ width: 70 }}
                          />
                        ) : (
                          <span style={{ fontWeight: 600 }}>{emp.total_days ?? '—'}</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {emp.periods.map((p, i) => {
                            const periodKind = periodConflictKind(
                              emp.employee_id,
                              p,
                              displayConflicts,
                              editMode ? freshConflicts : []
                            );
                            const periodColor = periodKind
                              ? CONFLICT_COLORS[periodKind]
                              : getVacationColor(p);
                            return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: periodColor,
                                  marginRight: 4,
                                  flexShrink: 0,
                                  boxShadow: `0 0 0 2px ${periodColor}30`,
                                }}
                              />
                              {editMode ? (
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <input
                                    type='date'
                                    value={formatDateInput(p.start)}
                                    onChange={(e) => updatePeriod(emp.name, i, 'start', parseDateInput(e.target.value))}
                                    className='form-input'
                                    style={{ width: 130 }}
                                  />
                                  <input
                                    type='number'
                                    value={p.days ?? ''}
                                    onChange={(e) => updatePeriod(emp.name, i, 'days', e.target.value ? Number(e.target.value) : null)}
                                    className='form-input'
                                    style={{ width: 55 }}
                                    placeholder='Дней'
                                  />
                                  <input
                                    type='date'
                                    value={formatDateInput(p.end)}
                                    onChange={(e) => updatePeriod(emp.name, i, 'end', parseDateInput(e.target.value))}
                                    className='form-input'
                                    style={{ width: 130 }}
                                  />
                                  <button className='btn-icon' onClick={() => removePeriod(emp.name, i)} title='Удалить период'>
                                    Удалить
                                  </button>
                                </div>
                              ) : (
                                (() => {
                                  const used = getPeriodStatus(p) === 'overdue';
                                  return (
                                    <span
                                      style={{
                                        textDecoration: used ? 'line-through' : 'none',
                                        color: used ? 'var(--text-muted)' : undefined,
                                        opacity: used ? 0.65 : 1,
                                      }}
                                      title={used ? 'Период использован' : undefined}
                                    >
                                      {formatDate(p.start)} — {formatDate(p.end)}
                                      {p.days && (
                                        <span style={{ 
                                          color: getPeriodStatus(p) === 'active' ? '#10b981' : 'var(--text-muted)', 
                                          marginLeft: 4,
                                          fontWeight: getPeriodStatus(p) === 'active' ? 600 : 400,
                                        }}>
                                          ({p.days} дн.)
                                          {getPeriodStatus(p) === 'active' && ' | Сейчас'}
                                        </span>
                                      )}
                                    </span>
                                  );
                                })()
                              )}
                            </div>
                            );
                          })}
                          {editMode && (
                            <button className='btn btn-sm' onClick={() => addPeriod(emp.name)}>
                              + Добавить период
                            </button>
                          )}
                          {emp.periods.length === 0 && !editMode && (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600 }}>{used}</span>
                        {emp.total_days && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>
                            ({Math.round((used / emp.total_days) * 100)}%)
                          </span>
                        )}
                      </td>
                      <td>
                        <span 
                          className={emp.remaining !== null && emp.remaining < 0 ? 'score-label low' : ''}
                          style={{
                            display: 'inline-flex',
                            padding: '4px 12px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 700,
                            background: emp.remaining !== null && emp.remaining < 0 
                              ? 'rgba(239, 68, 68, 0.15)' 
                              : emp.remaining !== null && emp.remaining < 7 
                                ? 'rgba(245, 158, 11, 0.15)' 
                                : 'rgba(16, 185, 129, 0.15)',
                            color: emp.remaining !== null && emp.remaining < 0 
                              ? '#ef4444' 
                              : emp.remaining !== null && emp.remaining < 7 
                                ? '#f59e0b' 
                                : '#10b981',
                          }}
                        >
                          {emp.remaining ?? '—'}
                        </span>
                      </td>
                      <td>
                        {hasActive ? (
                          <span className='score-label high'>В отпуске</span>
                        ) : hasPlanned ? (
                          <span className='score-label medium'>Запланирован</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Нет планов</span>
                        )}
                      </td>
                      {editMode && <td />}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}
        </>
      )}

      <style>{`
        .vacation-calendar {
          animation: fadeIn 0.2s ease-out;
        }
        .vacation-view-toggle {
          display: inline-flex;
          gap: 4px;
        }
        .vacation-view-toggle--active {
          box-shadow: inset 0 0 0 2px var(--accent-primary);
        }
        .vacation-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 12px 16px;
          margin-bottom: 16px;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .vacation-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .vacation-legend-item i {
          width: 12px;
          height: 12px;
          border-radius: 3px;
          display: inline-block;
        }
        .vacation-month-nav {
          display: inline-flex;
          gap: 8px;
        }
        .vacation-conflict-banner {
          margin-bottom: 16px;
          padding: 12px 16px;
          border: 1px solid rgba(245, 158, 11, 0.45);
          border-radius: 10px;
          background: rgba(245, 158, 11, 0.12);
          color: var(--text-primary);
        }
        .vacation-conflict-banner--new {
          border-color: rgba(239, 68, 68, 0.5);
          background: rgba(239, 68, 68, 0.12);
        }
        .vacation-conflict-banner ul {
          margin: 8px 0 0;
          padding-left: 18px;
        }
        .vacation-conflict-banner li {
          font-size: 13px;
          line-height: 1.5;
        }
        .vacation-conflict-new-mark {
          color: #ef4444;
          font-weight: 700;
        }
        .vacation-table-row--conflict-existing td {
          background: rgba(245, 158, 11, 0.08);
        }
        .vacation-table-row--conflict-new td {
          background: rgba(239, 68, 68, 0.08);
        }
        .form-input {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 6px 10px;
          color: var(--text-primary);
          font-size: 13px;
        }
        .form-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(47, 158, 155, 0.15);
        }
        .btn-icon {
          background: var(--bg-glass);
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 12px;
          padding: 6px 8px;
          border-radius: 6px;
        }
        .btn-icon:hover {
          color: var(--text-primary);
          background: var(--bg-glass-hover);
        }
      `}</style>
    </div>
  );
}
